import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

export interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  actionTo?: string;
  onAction?: () => void;
}

/**
 * EmptyState - A reusable component for displaying empty states
 *
 * Provides consistent styling and layout for "no data" states
 * throughout the application.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  actionTo,
  onAction,
}) => (
  <Box
    sx={(theme) => ({
      background:
        theme.palette.mode === 'light'
          ? 'rgba(34,181,220,.06)'
          : 'rgba(34,181,220,.12)',
      border: `1px solid ${
        theme.palette.mode === 'light'
          ? 'rgba(34,181,220,.20)'
          : 'rgba(34,181,220,.30)'
      }`,
      borderRadius: '12px',
      padding: '20px 24px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    })}
  >
    {icon && (
      <Box
        sx={(theme) => ({
          color:
            theme.palette.mode === 'light'
              ? theme.palette.brand.teal
              : theme.palette.brand.cyan,
          display: 'flex',
          flexShrink: 0,
          '& > svg': { fontSize: 20 },
        })}
      >
        {icon}
      </Box>
    )}
    <Box sx={{ flex: 1 }}>
      <Typography
        sx={(theme) => ({
          fontSize: 13,
          fontWeight: 600,
          color: theme.palette.text.primary,
          marginBottom: description ? '4px' : 0,
        })}
      >
        {title}
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: 12.5,
          color: theme.palette.text.secondary,
          lineHeight: 1.5,
        })}
      >
        {description}
      </Typography>
      {actionLabel && (actionTo || onAction) && (
        <Box sx={{ marginTop: '12px' }}>
          <Button
            component={actionTo ? Link : 'button'}
            to={actionTo}
            onClick={onAction}
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
          >
            {actionLabel}
          </Button>
        </Box>
      )}
    </Box>
  </Box>
);

export default EmptyState;
